<template>
  <div class="checkout-address-selector">
    <!-- Loading State -->
    <div v-if="loading" class="address-loader py-4">
      <div class="spinner-border spinner-border-sm text-dark" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 small text-muted">{{ t('account.addresses.loading') }}</p>
    </div>

    <div v-else>
      <!-- Address Selection List -->
      <div v-if="!showAddForm" class="address-grid">
        <div 
          v-for="address in sortedAddresses" 
          :key="address.addressId"
          class="address-option-card"
          :class="{ 'is-selected': selectedId === address.addressId }"
          @click="selectAddress(address.addressId)"
        >
          <div class="selection-indicator">
            <div class="radio-circle"></div>
          </div>
          
          <div class="address-content">
            <div class="d-flex justify-content-between align-items-start mb-1">
              <span class="name fw-bold">{{ (address.firstName && address.firstName.trim()) || (address.lastName && address.lastName.trim()) ? `${address.firstName || ''} ${address.lastName || ''}`.trim() : address.street }}</span>
              <span v-if="address.isDefault" class="badge-default">{{ t('account.addresses.default') }}</span>
            </div>
            <p class="mb-0 street">{{ address.street }}</p>
            <p class="mb-0 city-zip">{{ address.zipCode }} {{ address.city }} <span v-if="address.stateProvince && address.stateProvince.trim() !== ''">({{ address.stateProvince }})</span></p>
            <p class="mb-0 country">{{ address.country }}</p>
            <p v-if="address.phone" class="mb-0 mt-1 phone small text-muted">
              <span class="text-uppercase letter-spacing-1 me-1">{{ t('account.addresses.phone') }}:</span>
              {{ address.phone }}
            </p>
            <p v-if="address.notes" class="mt-2 notes small text-muted fst-italic">"{{ address.notes }}"</p>
          </div>
        </div>

        <!-- Add New Address Button (Card style) -->
        <button class="add-address-card" @click="showAddForm = true">
          <div class="add-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </div>
          <span>{{ t('account.addresses.add_new') }}</span>
        </button>
      </div>

      <!-- Add New Address Form (Inline) -->
      <div v-else ref="addFormRef" class="add-address-form border p-4 bg-light">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="h6 text-uppercase fw-bold mb-0 letter-spacing-1">{{ t('account.addresses.add_title') }}</h3>
          <button @click="showAddForm = false" class="btn-close-form">
            {{ t('account.personal_data.btn.cancel') }}
          </button>
        </div>

        <form @submit.prevent="saveNewAddress" class="premium-form">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.personal_data.label.name') }}</label>
                <input v-model="form.firstName" type="text" class="form-control form-control-sm" required>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.personal_data.label.surname') }}</label>
                <input v-model="form.lastName" type="text" class="form-control form-control-sm" required>
              </div>
            </div>
            <div class="col-12">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.personal_data.label.address') }}</label>
                <input v-model="form.street" type="text" class="form-control form-control-sm" required>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.personal_data.label.postalCode') }}</label>
                <input v-model="form.zipCode" type="text" class="form-control form-control-sm" required>
              </div>
            </div>
            <div class="col-md-8">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.personal_data.label.city') }}</label>
                <input v-model="form.city" type="text" class="form-control form-control-sm" required>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.addresses.province') }}</label>
                <input v-model="form.stateProvince" type="text" class="form-control form-control-sm" required>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.personal_data.label.country') }}</label>
                <input v-model="form.country" type="text" class="form-control form-control-sm" required>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.addresses.phone') }}</label>
                <input
                  v-model="form.phone"
                  type="tel"
                  class="form-control form-control-sm"
                  inputmode="tel"
                  autocomplete="tel"
                  pattern="^\+?[0-9\s-]{8,20}$"
                  maxlength="20"
                >
              </div>
            </div>
            <div class="col-12">
              <div class="form-group">
                <label class="form-label-compact">{{ t('account.addresses.notes_label') }}</label>
                <textarea v-model="form.notes" class="form-control form-control-sm" rows="2"></textarea>
              </div>
            </div>
            <div class="col-12">
              <div class="default-address-toggle" @click="form.isDefault = !form.isDefault">
                <div class="toggle-switch" :class="{ 'is-active': form.isDefault }">
                  <div class="toggle-handle"></div>
                </div>
                <span class="toggle-label">{{ t('account.addresses.set_default') }}</span>
              </div>
            </div>
          </div>
          
          <div class="mt-4">
            <button type="submit" class="btn btn-dark w-100" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ t('account.personal_data.btn.save') }} E SELEZIONA
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import apiClient from '@/services/api'
import { useLanguage } from '@/composables/useLanguage'

const props = defineProps({
  modelValue: [Number, String]
})

const emit = defineEmits(['update:modelValue', 'change'])

const { t } = useLanguage()

const addresses = ref([])
const loading = ref(true)
const saving = ref(false)
const showAddForm = ref(false)
const selectedId = ref(props.modelValue)
const addFormRef = ref(null)

const initialForm = {
  firstName: '',
  lastName: '',
  addressType: 'Shipping',
  companyName: '',
  street: '',
  city: '',
  zipCode: '',
  stateProvince: '',
  country: 'Italy',
  phone: '',
  isDefault: false,
  notes: ''
}

const form = ref({ ...initialForm })

const sortedAddresses = computed(() => {
  return [...addresses.value].sort((a, b) => {
    if (a.isDefault) return -1
    if (b.isDefault) return 1
    return 0
  })
})

const fetchAddresses = async () => {
  loading.value = true
  try {
    const response = await apiClient.get('/CustomerAddresses')
    addresses.value = response.data.data || []
    
    if (addresses.value.length > 0) {
      if (!selectedId.value) {
        const defaultAddr = addresses.value.find(a => a.isDefault)
        if (defaultAddr) {
          selectAddress(defaultAddr.addressId)
        } else {
          selectAddress(addresses.value[0].addressId)
        }
      } else {
        const exists = addresses.value.some(a => a.addressId === selectedId.value)
        if (!exists) {
          const defaultAddr = addresses.value.find(a => a.isDefault)
          selectAddress(defaultAddr ? defaultAddr.addressId : addresses.value[0].addressId)
        }
      }
    } else {
      showAddForm.value = true
    }
  } catch (error) {
    console.error('[CheckoutAddressSelector] Error:', error)
  } finally {
    loading.value = false
  }
}

const selectAddress = (id) => {
  selectedId.value = id
  emit('update:modelValue', id)
  const address = addresses.value.find(a => a.addressId === id)
  emit('change', address)
}

const saveNewAddress = async () => {
  saving.value = true
  try {
    const payload = { ...form.value, phone: (form.value.phone || '').trim() || null }
    const response = await apiClient.post('/CustomerAddresses', payload)
    const newAddress = response.data
    
    await fetchAddresses()
    
    if (newAddress && newAddress.addressId) {
      selectAddress(newAddress.addressId)
    }
    
    showAddForm.value = false
    form.value = { ...initialForm }
  } catch (error) {
    console.error('[CheckoutAddressSelector] Error saving:', error)
  } finally {
    saving.value = false
  }
}

watch(() => props.modelValue, (newVal) => {
  selectedId.value = newVal
})

watch(showAddForm, (val) => {
  if (val) {
    nextTick(() => {
      addFormRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }
})

onMounted(() => {
  fetchAddresses()
})
</script>

<style scoped>
.address-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}

.address-option-card {
  border: 1px solid #eee;
  padding: 20px;
  cursor: pointer;
  display: flex;
  gap: 15px;
  transition: all 0.3s ease;
  background: #fff;
  position: relative;
}

.address-option-card:hover {
  border-color: #000;
}

.address-option-card.is-selected {
  border-color: #5C8889; /* Premium Teal */
  background-color: #f8fbfa;
  box-shadow: 0 4px 15px rgba(92, 136, 137, 0.1);
}

.selection-indicator {
  padding-top: 3px;
}

.radio-circle {
  width: 18px;
  height: 18px;
  border: 2px solid #ddd;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease;
}

.is-selected .radio-circle {
  border-color: #5C8889;
}

.is-selected .radio-circle::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 8px;
  height: 8px;
  background: #5C8889;
  border-radius: 50%;
}

.address-content {
  flex: 1;
}

.address-content .name {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.address-content p {
  font-size: 13px;
  color: #555;
  line-height: 1.4;
}

.badge-default {
  font-size: 8px;
  background: #eee;
  padding: 2px 6px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.is-selected .badge-default {
  background: #5C8889;
  color: #fff;
}

.add-address-card {
  border: 1px dashed #ccc;
  background: #fafafa;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 120px;
}

.add-address-card:hover {
  background: #fff;
  border-color: #000;
}

.add-address-card span {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.add-icon {
  color: #888;
}

.btn-close-form {
  background: none;
  border: none;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  cursor: pointer;
  letter-spacing: 1px;
}

.btn-close-form:hover {
  color: #000;
}

.form-label-compact {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #888;
  margin-bottom: 5px;
  display: block;
}

.letter-spacing-1 { letter-spacing: 1.5px; }

.address-loader {
  text-align: center;
}

/* Modern Toggle Switch Style */
.default-address-toggle {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px 0;
  cursor: pointer;
  user-select: none;
  border-top: 1px solid #f0f0f0;
  margin-top: 10px;
}

.toggle-switch {
  width: 40px;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 20px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toggle-switch.is-active {
  background-color: #5C8889; /* Premium Teal */
}

.toggle-handle {
  width: 14px;
  height: 14px;
  background-color: #fff;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: 3px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.is-active .toggle-handle {
  left: 23px;
}

.toggle-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #111;
}

.default-address-toggle:hover .toggle-switch {
  opacity: 0.8;
}
</style>
